import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

/** The 3 ja-JP neural voices we generate. */
export const VOICES = ['Takumi', 'Kazuha', 'Tomoko'] as const;
export type VoiceId = (typeof VOICES)[number];

export const VOICE_LABEL: Record<VoiceId, string> = {
  Takumi: 'Takumi (M)',
  Kazuha: 'Kazuha (F)',
  Tomoko: 'Tomoko (F)',
};

/**
 * Voice slots — three named buckets the user assigns voices to.
 * - `staff`: dialogue lines spoken by service staff / non-learner roles
 * - `you`: dialogue lines spoken by the learner role
 * - `teacher`: vocab readings, grammar examples, prose phrases — anything outside dialogue
 */
export type VoiceRole = 'staff' | 'you' | 'teacher';
export const VOICE_ROLES: VoiceRole[] = ['teacher', 'you', 'staff'];

export const ROLE_LABEL: Record<VoiceRole, string> = {
  staff: 'Staff',
  you: 'You',
  teacher: 'Teacher',
};

/**
 * Sample phrases used in the voice-picker UI to preview each voice in each
 * role's typical context. These strings are explicitly added to the audio
 * inventory so they're guaranteed to exist for all 3 voices after Polly runs.
 *
 * `ROLE_SAMPLE_HASH` is the precomputed audioHash() for each phrase — kept in
 * sync manually since hashing in the browser would mean async/Web Crypto.
 * Recompute with `node --experimental-strip-types -e "..."` if these strings
 * are changed.
 */
export const ROLE_SAMPLE: Record<VoiceRole, string> = {
  staff: 'いらっしゃいませ',
  you: 'お願いします',
  teacher: '一つ、二つ、三つ',
};

export const ROLE_SAMPLE_HASH: Record<VoiceRole, string> = {
  staff: '91427722fc2ad159',
  you: 'c5c3df248eaaa492',
  teacher: 'dfe216d69e2882e8',
};

export type VoicePrefs = Record<VoiceRole, VoiceId>;

const DEFAULT_PREFS: VoicePrefs = {
  staff: 'Kazuha',
  you: 'Takumi',
  teacher: 'Takumi',
};

const STORAGE_KEY = 'voices';

type Ctx = {
  prefs: VoicePrefs;
  setVoice: (role: VoiceRole, voice: VoiceId) => void;
  swap: () => void;
  reset: () => void;
};

const VoiceContext = createContext<Ctx | null>(null);

function load(): VoicePrefs {
  if (typeof localStorage === 'undefined') return DEFAULT_PREFS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<VoicePrefs>;
    const isVoice = (v: unknown): v is VoiceId =>
      typeof v === 'string' && (VOICES as readonly string[]).includes(v);
    return {
      staff: isVoice(parsed.staff) ? parsed.staff : DEFAULT_PREFS.staff,
      you: isVoice(parsed.you) ? parsed.you : DEFAULT_PREFS.you,
      teacher: isVoice(parsed.teacher) ? parsed.teacher : DEFAULT_PREFS.teacher,
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

export function VoicePrefsProvider({ children }: { children: ReactNode }) {
  const [prefs, setPrefs] = useState<VoicePrefs>(load);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  }, [prefs]);

  const setVoice = useCallback((role: VoiceRole, voice: VoiceId) => {
    setPrefs((p) => ({ ...p, [role]: voice }));
  }, []);

  const swap = useCallback(() => {
    setPrefs((p) => ({ ...p, staff: p.you, you: p.staff }));
  }, []);

  const reset = useCallback(() => setPrefs(DEFAULT_PREFS), []);

  return (
    <VoiceContext.Provider value={{ prefs, setVoice, swap, reset }}>
      {children}
    </VoiceContext.Provider>
  );
}

export function useVoicePrefs(): Ctx {
  const ctx = useContext(VoiceContext);
  if (!ctx) throw new Error('useVoicePrefs must be used within VoicePrefsProvider');
  return ctx;
}

/** Resolve the voice id for a given role using current prefs. */
export function useVoice(role: VoiceRole): VoiceId {
  const { prefs } = useVoicePrefs();
  return prefs[role];
}

/** Build the mp3 filename for a (hash, voice) pair. */
export function audioFilename(hash: string, voice: VoiceId): string {
  return `${hash}_${voice}.mp3`;
}

/** Map an arbitrary lesson role key to one of our voice slots. */
export function voiceRoleForTurn(turnRole: string): VoiceRole {
  return turnRole === 'you' ? 'you' : 'staff';
}
